/**
 * @file Client-side route table.
 * @module @labcraft/lab/ui/routes
 *
 * Every route renders inside the {@link LabShell} layout — the
 * shell owns the chrome (the lab `LabAppBar` + `Footer`, the latter
 * dropped in the full-bleed reader) and the cross-page
 * `SessionExpiredWatcher` effect; routed bodies render into its
 * `<Outlet />`. Mirrors the admin (`AdminShell`) and www
 * (`SiteLayout`) pattern.
 *
 * Public routes:
 *
 *   - `/` — landing (marketing + embedded course excerpt demo).
 *   - `/manifesto` — long-form positioning prose.
 *   - `/contact` — terminal-styled compose form.
 *   - `/authorize` — single auth entry point. Sign-up and sign-in
 *     collapse here; the backend resolves new vs returning via
 *     `findOrLinkByClaims`. `/signin` is a legacy alias that
 *     redirects to `/authorize`.
 *   - `/auth/{sent,verify,expired}` — magic-link landing flow.
 *
 * Authenticated routes (status-gated):
 *
 *   - `/account/inactive` — landing for `status === 'inactive'`.
 *   - `/account/update` — display-name form (and any future profile
 *     questions). Requires `status === 'active'`.
 *   - `/courses` — course picker (the natural landing for active +
 *     profile-complete learners).
 *   - `/courses/:courseId` — course landing page (overview, parts
 *     list, progress placeholder). Mirrors `GET /api/courses/:courseId`
 *     once #368 adds the API twin.
 *   - `/courses/:courseId/:partId/assessment` — the Part-boundary CAT
 *     mastery assessment (umbrella #1357). Registered ABOVE the dynamic
 *     `:chapterId` route so the static `assessment` segment wins.
 *   - `/courses/:courseId/:partId/:chapterId` — the chapter reader.
 *     URL-isomorphic with lab's combined chapter endpoint at
 *     `/api/courses/:courseId/:partId/:chapterId` (umbrella #358).
 *
 * Unknown paths bounce to `/courses` (the picker). The SPA fallback
 * that serves this tree in production is owned by the gateway's
 * `HttpService.public` + `serveIndex()`.
 */

import { useEffect, type JSX, type ReactNode } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router'
import { resolveLandingForLearner } from '../lib/landing.ts'
import { useMe } from '../hooks/use_me.ts'
import { LabShell } from '../components/lab_shell.tsx'
import { AuthExpiredPage } from '../pages/auth/expired.tsx'
import { AuthSentPage } from '../pages/auth/sent.tsx'
import { AuthVerifyPage } from '../pages/auth/verify.tsx'
import { SigninPage } from '../pages/auth/authorize.tsx'
import { ChapterPage } from '../pages/chapter.tsx'
import { PartAssessmentPage } from '../pages/part_assessment.tsx'
import { ReaderErrorBoundary } from '../components/reader_error_boundary.tsx'
import { HomePage } from '../pages/home.tsx'
import { CatalogPage } from '../pages/catalog.tsx'
import { CourseLandingPage } from '../pages/course_landing.tsx'
import { CoursesPage } from '../pages/courses.tsx'
import { AccountUpdatePage } from '../pages/account/update.tsx'
import { AccountInactivePage } from '../pages/account/inactive.tsx'

/**
 * Root router. Mounted inside the top-level `<ClientProvider>` in
 * `main.tsx` so every route has access to `useClient<Lab>()`.
 */
export function AppRouter(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LabShell />}>
          <Route
            path="/"
            element={
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            }
          />
          <Route path="/authorize" element={<SigninPage />} />
          {/*
           * `/signin` → `/authorize` permanent redirect. Sign-up and
           * sign-in are the same flow now (the backend's
           * `findOrLinkByClaims` resolves new vs returning on the
           * verified claims); the visible verb on every surface is
           * "Sign in." `/signin` stays alive for legacy bookmarks and
           * preserves its `?next=…` query through the bounce.
           */}
          <Route
            path="/signin"
            element={
              <Navigate to={`/authorize${window.location.search}`} replace />
            }
          />
          <Route
            path="/signin/*"
            element={<Navigate to="/authorize" replace />}
          />
          <Route path="/auth/sent" element={<AuthSentPage />} />
          <Route path="/auth/verify" element={<AuthVerifyPage />} />
          <Route path="/auth/expired" element={<AuthExpiredPage />} />
          <Route
            path="/account/inactive"
            element={
              <RequireSignedIn>
                <AccountInactivePage />
              </RequireSignedIn>
            }
          />
          <Route
            path="/account/update"
            element={
              <RequireApproved>
                <AccountUpdatePage />
              </RequireApproved>
            }
          />
          <Route
            path="/catalog"
            element={
              <AuthGuard>
                <CatalogPage />
              </AuthGuard>
            }
          />
          <Route
            path="/courses"
            element={
              <AuthGuard>
                <CoursesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <AuthGuard>
                <CourseLandingPage />
              </AuthGuard>
            }
          />
          {/* Registered ABOVE the dynamic `:chapterId` route so the static
              `assessment` segment wins the match (umbrella #1357). */}
          <Route
            path="/courses/:courseId/:partId/assessment"
            element={
              <AuthGuard>
                <ReaderErrorBoundary>
                  <PartAssessmentPage />
                </ReaderErrorBoundary>
              </AuthGuard>
            }
          />
          <Route
            path="/courses/:courseId/:partId/:chapterId"
            element={
              <AuthGuard>
                <ReaderErrorBoundary>
                  <ChapterPage />
                </ReaderErrorBoundary>
              </AuthGuard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Route guards
////////////////////////////////////////////////////////////////////////////////

/**
 * Wrap a route that requires `status === 'active'` AND a completed
 * profile. The full status gate — `/courses/*`. Each pre-condition
 * that fails sends the learner to its dedicated landing with `next`
 * preserved.
 */
function AuthGuard(props: { children: ReactNode }): JSX.Element | null {
  const me = useMe()
  const navigate = useNavigate()
  const location = useLocation()
  const learner = me.data?.learner ?? null

  useEffect(() => {
    // Don't bounce while the first /me fetch is in flight — the
    // initial render returns `learner === null` until the query
    // settles. `me.isPending` covers cold-load; subsequent reads
    // are synchronous from cache.
    if (me.isPending) return
    const here = location.pathname + location.search
    if (!learner) {
      navigate(`/authorize?next=${encodeURIComponent(here)}`, {
        replace: true,
      })
      return
    }
    const target = resolveLandingForLearner(learner, here)
    if (target !== here) {
      navigate(target, { replace: true })
    }
  }, [learner, me.isPending, navigate, location.pathname, location.search])

  if (me.isPending) return null
  if (!learner) return null
  if (learner.status !== 'active') return null
  if (learner.profileCompletedAt == null) return null
  return <>{props.children}</>
}

/**
 * Wrap a route that requires only "signed in" — used by
 * `/account/inactive`. Inactive learners land here and stay;
 * active learners get bounced onward.
 */
function RequireSignedIn(props: { children: ReactNode }): JSX.Element | null {
  const me = useMe()
  const navigate = useNavigate()
  const learner = me.data?.learner ?? null

  useEffect(() => {
    if (me.isPending) return
    if (!learner) {
      navigate('/authorize', { replace: true })
      return
    }
    // Active learners don't belong here — drop them at their
    // natural landing.
    if (learner.status === 'active') {
      navigate(resolveLandingForLearner(learner, '/'), {
        replace: true,
      })
    }
  }, [learner, me.isPending, navigate])

  if (me.isPending) return null
  if (!learner) return null
  if (learner.status === 'active') return null
  return <>{props.children}</>
}

/**
 * Wrap a route that requires "signed in + active" but NOT
 * necessarily profile-complete — used by `/account/update` so the
 * post-activation first-time-setup flow can reach it.
 */
function RequireApproved(props: { children: ReactNode }): JSX.Element | null {
  const me = useMe()
  const navigate = useNavigate()
  const location = useLocation()
  const learner = me.data?.learner ?? null

  useEffect(() => {
    if (me.isPending) return
    if (!learner) {
      const here = location.pathname + location.search
      navigate(`/authorize?next=${encodeURIComponent(here)}`, {
        replace: true,
      })
      return
    }
    if (learner.status === 'banned') {
      navigate('/?banned=1', { replace: true })
      return
    }
    if (learner.status === 'inactive') {
      navigate('/account/inactive', { replace: true })
    }
  }, [learner, me.isPending, navigate, location.pathname, location.search])

  if (me.isPending) return null
  if (!learner) return null
  if (learner.status !== 'active') return null
  return <>{props.children}</>
}
